/**
 * ASOResultsViz - ASO design results visualization for chat mode.
 *
 * Displays ASO candidates with sequences, design metrics, and recommendations.
 */

import React, { useState } from 'react';
import InfoTooltip from './InfoTooltip';

interface ASOCandidate {
  sequence: string;
  position: number;
  length: number;
  gc_content: number;
  tm: number;
  specificity_score: number;
  region_accessibility: number;
}

interface ASOResultsVizProps {
  data: {
    type: 'aso';
    gene: string;
    sequence_info?: {
      id: string;
      length: number;
      description?: string;
    };
    top_candidates: ASOCandidate[];
    design_criteria?: {
      min_length: number;
      max_length: number;
      optimal_length: number;
      min_gc: number;
      max_gc: number;
      optimal_gc: number;
      min_tm: number;
      max_tm: number;
    };
    recommendations?: string[];
    accessible_regions?: Array<{ start: number; end: number; gc: number }>;
  };
  onAction?: (action: string, params?: any) => void;
}

export const ASOResultsViz: React.FC<ASOResultsVizProps> = ({ data, onAction }) => {
  const [selectedASO, setSelectedASO] = useState<ASOCandidate | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const copyToClipboard = (sequence: string, index: number) => {
    navigator.clipboard.writeText(sequence);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-400';
    if (score >= 0.6) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 0.8) return 'bg-green-900/30 text-green-400';
    if (score >= 0.6) return 'bg-yellow-900/30 text-yellow-400';
    return 'bg-red-900/30 text-red-400';
  };

  return (
    <div className="origin-card mt-4 p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-1">
          💊 ASO Design Results
        </h3>
        <p className="text-sm text-gray-400">
          Target: <span className="font-mono font-semibold text-blue-400">{data.gene}</span>
        </p>
        {data.sequence_info && (
          <p className="text-xs text-gray-500 mt-1">
            Sequence: {data.sequence_info.id} ({data.sequence_info.length} nt)
          </p>
        )}
      </div>

      {/* Summary Stats */}
      {data.top_candidates && data.top_candidates.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 bg-blue-900/30 rounded-lg border border-blue-800">
            <div className="text-xs text-gray-400">Candidates</div>
            <div className="text-2xl font-bold text-blue-400">{data.top_candidates.length}</div>
          </div>
          <div className="p-3 bg-green-900/30 rounded-lg border border-green-800">
            <div className="text-xs text-gray-400">Top Score</div>
            <div className="text-2xl font-bold text-green-400">
              {(data.top_candidates[0]?.specificity_score * 100).toFixed(0)}%
            </div>
          </div>
          {data.accessible_regions && (
            <div className="p-3 bg-purple-950/30 rounded-lg border border-purple-800">
              <div className="text-xs text-gray-400">Accessible Regions</div>
              <div className="text-2xl font-bold text-purple-400">{data.accessible_regions.length}</div>
            </div>
          )}
        </div>
      )}

      {/* ASO Candidates Table */}
      {data.top_candidates && data.top_candidates.length > 0 ? (
        <div className="max-h-[500px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900 border-b-2 border-gray-600">
              <tr>
                <th className="text-left py-2 px-3">#</th>
                <th className="text-left py-2 px-3">Sequence (5' → 3')</th>
                <th className="text-right py-2 px-3">
                  <span className="flex items-center justify-end gap-1">
                    Position
                    <InfoTooltip title="Position" content="Start nucleotide position on the target RNA transcript (0-indexed)." position="right" />
                  </span>
                </th>
                <th className="text-right py-2 px-3">Length</th>
                <th className="text-right py-2 px-3">
                  <span className="flex items-center justify-end gap-1">
                    GC%
                    <InfoTooltip title="GC Content" content="Percentage of guanine+cytosine bases. Optimal range 40–60% (shown green) balances stability and specificity." position="right" />
                  </span>
                </th>
                <th className="text-right py-2 px-3">
                  <span className="flex items-center justify-end gap-1">
                    Tm (°C)
                    <InfoTooltip title="Melting Temperature" content="Predicted melting temperature. Optimal range 50–65°C. Higher Tm = more stable ASO-RNA duplex." position="right" />
                  </span>
                </th>
                <th className="text-right py-2 px-3">
                  <span className="flex items-center justify-end gap-1">
                    Score
                    <InfoTooltip title="Design Score" content="Composite score (0–100%) based on GC content, melting temperature, and RNA accessibility. Green ≥ 80%, yellow ≥ 60%, red < 60%." position="right" />
                  </span>
                </th>
                <th className="text-center py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.top_candidates.map((aso, idx) => (
                <tr
                  key={idx}
                  className={`border-b border-gray-700 hover:bg-blue-900/20 transition-colors cursor-pointer ${
                    selectedASO === aso ? 'bg-blue-900/30' : ''
                  }`}
                  onClick={() => setSelectedASO(aso === selectedASO ? null : aso)}
                >
                  <td className="py-2 px-3 font-semibold text-gray-400">
                    {idx + 1}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">
                    <span className="bg-gray-800 px-2 py-1 rounded">
                      {aso.sequence}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right text-xs text-gray-400">
                    {aso.position}-{aso.position + aso.length}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={`text-xs ${
                      aso.length === data.design_criteria?.optimal_length
                        ? 'text-green-400 font-semibold'
                        : 'text-gray-400'
                    }`}>
                      {aso.length} nt
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={`text-xs ${
                      aso.gc_content >= data.design_criteria?.min_gc! &&
                      aso.gc_content <= data.design_criteria?.max_gc!
                        ? 'text-green-400 font-semibold'
                        : 'text-gray-400'
                    }`}>
                      {aso.gc_content.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={`text-xs ${
                      aso.tm >= data.design_criteria?.min_tm! &&
                      aso.tm <= data.design_criteria?.max_tm!
                        ? 'text-green-400 font-semibold'
                        : 'text-gray-400'
                    }`}>
                      {aso.tm.toFixed(1)}°C
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className={`text-xs px-2 py-1 rounded font-semibold ${getScoreBadge(aso.specificity_score)}`}>
                      {(aso.specificity_score * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(aso.sequence, idx);
                      }}
                      className="text-xs px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded transition-colors"
                      title="Copy sequence"
                    >
                      {copiedIndex === idx ? '✓ Copied' : '📋 Copy'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4 bg-gray-800 rounded-lg text-center text-gray-400">
          No ASO candidates found
        </div>
      )}

      {/* Selected ASO Details */}
      {selectedASO && (
        <div className="mt-4 p-4 bg-blue-900/30 border border-blue-800 rounded-lg">
          <h4 className="text-sm font-semibold mb-2 text-blue-300">Selected ASO Details</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-400">Sequence:</span>
              <div className="font-mono text-xs bg-gray-800 px-2 py-1 rounded mt-1 break-all">
                {selectedASO.sequence}
              </div>
            </div>
            <div>
              <span className="text-gray-400">Position:</span>
              <div className="font-semibold mt-1">
                {selectedASO.position}-{selectedASO.position + selectedASO.length}
              </div>
            </div>
            <div>
              <span className="text-gray-400">GC Content:</span>
              <div className="font-semibold mt-1">{selectedASO.gc_content.toFixed(1)}%</div>
            </div>
            <div>
              <span className="text-gray-400">Melting Temperature:</span>
              <div className="font-semibold mt-1">{selectedASO.tm.toFixed(1)}°C</div>
            </div>
            <div>
              <span className="text-gray-400">Accessibility:</span>
              <div className="font-semibold mt-1">{(selectedASO.region_accessibility * 100).toFixed(0)}%</div>
            </div>
            <div>
              <span className="text-gray-400">Overall Score:</span>
              <div className={`font-bold mt-1 ${getScoreColor(selectedASO.specificity_score)}`}>
                {(selectedASO.specificity_score * 100).toFixed(0)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Design Criteria */}
      {data.design_criteria && (
        <div className="mt-4 p-4 bg-gray-800 rounded-lg">
          <div className="flex items-center gap-1.5 mb-2">
            <h4 className="text-sm font-semibold text-gray-300">Design Criteria</h4>
            <InfoTooltip title="Design Criteria" content="Based on RNase H-mediated ASO design principles. Optimal length 18–22 nt ensures specificity. GC 40–60% and Tm 50–65°C balance binding affinity and selectivity." />
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div>
              <span className="text-gray-400">Length:</span>
              <div className="font-semibold">
                {data.design_criteria.min_length}-{data.design_criteria.max_length} nt
                <span className="text-green-400 ml-1">(optimal: {data.design_criteria.optimal_length})</span>
              </div>
            </div>
            <div>
              <span className="text-gray-400">GC Content:</span>
              <div className="font-semibold">
                {data.design_criteria.min_gc}-{data.design_criteria.max_gc}%
                <span className="text-green-400 ml-1">(optimal: {data.design_criteria.optimal_gc}%)</span>
              </div>
            </div>
            <div>
              <span className="text-gray-400">Tm Range:</span>
              <div className="font-semibold">
                {data.design_criteria.min_tm}-{data.design_criteria.max_tm}°C
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="mt-4 p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
          <h4 className="text-sm font-semibold mb-2 text-yellow-300">⚠️ Next Steps & Recommendations</h4>
          <ul className="text-xs text-gray-300 space-y-1">
            {data.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start">
                <span className="mr-2">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Interpretation */}
      <div className="mt-4 p-4 bg-blue-900/30 border border-blue-800 rounded-lg text-sm">
        <p className="font-semibold text-blue-300 mb-2">📘 Interpretation:</p>
        <p className="text-gray-300">
          {data.top_candidates.length > 0 ? (
            <>
              Designed <span className="font-semibold">{data.top_candidates.length} ASO candidates</span> for{' '}
              <span className="font-semibold text-blue-400">{data.gene}</span>. The top candidate has a{' '}
              <span className={`font-semibold ${getScoreColor(data.top_candidates[0].specificity_score)}`}>
                {(data.top_candidates[0].specificity_score * 100).toFixed(0)}% score
              </span>{' '}
              based on length, GC content, melting temperature, and accessibility. These are computational predictions
              - validate experimentally before synthesis.
            </>
          ) : (
            'No suitable ASO candidates found for this target. The RNA sequence may not have accessible regions that meet design criteria.'
          )}
        </p>
      </div>
    </div>
  );
};

export default ASOResultsViz;
